import React, { useState } from 'react';
import { useAdminTheme } from '../contexts/AdminThemeContext';
import Icon from './AppIcon';
import RichTextEditor from './RichTextEditor';

const EnhancedResourcesManagement = () => {
  console.log('🚀 EnhancedResourcesManagement component initializing');
  const { currentColors } = useAdminTheme();
  const [resources, setResources] = useState([
    {
      id: '1',
      title: 'GCC Setup & Management Best Practices',
      description: 'Comprehensive guide for establishing successful Global Capability Centers',
      content: '# GCC Setup Guide\n\nThis guide covers all aspects of setting up a GCC...',
      content_type: 'guide',
      category: 'strategy',
      tags: ['GCC', 'Setup', 'Management'],
      featured_image: '/assets/images/gcc-guide.jpg',
      estimated_read_time: 15,
      difficulty_level: 'intermediate',
      is_featured: true,
      status: 'published',
      slug: 'gcc-setup-management-best-practices',
      created_at: '2024-01-15T10:30:00.000Z',
      updated_at: '2024-01-15T10:30:00.000Z'
    },
    {
      id: '2',
      title: 'Digital Transformation Strategies for 2024',
      description: 'Cutting-edge strategies for digital transformation in global organizations',
      content: '# Digital Transformation 2024\n\nExplore the latest trends and strategies...',
      content_type: 'report',
      category: 'technology',
      tags: ['Digital', 'Transformation', 'Strategy'],
      featured_image: '/assets/images/digital-transformation.jpg',
      estimated_read_time: 20,
      difficulty_level: 'advanced',
      is_featured: false,
      status: 'published',
      slug: 'digital-transformation-strategies-2024',
      created_at: '2024-01-20T14:15:00.000Z',
      updated_at: '2024-01-20T14:15:00.000Z'
    },
    {
      id: '3',
      title: 'Talent Acquisition in Global Markets',
      description: 'Master the art of recruiting top talent across international markets',
      content: '# Global Talent Acquisition\n\nStrategies for finding and attracting global talent...',
      content_type: 'guide',
      category: 'talent',
      tags: ['Talent', 'Recruitment', 'Global'],
      featured_image: '/assets/images/talent-acquisition.jpg',
      estimated_read_time: 12,
      difficulty_level: 'intermediate',
      is_featured: false,
      status: 'published',
      slug: 'talent-acquisition-global-markets',
      created_at: '2024-01-25T09:45:00.000Z',
      updated_at: '2024-01-25T09:45:00.000Z'
    },
    {
      id: '4',
      title: 'Cloud Infrastructure Optimization',
      description: 'Learn to optimize cloud costs while maximizing performance and security',
      content: '# Cloud Optimization\n\nBest practices for cloud infrastructure management...',
      content_type: 'whitepaper',
      category: 'technology',
      tags: ['Cloud', 'Infrastructure', 'Optimization'],
      featured_image: '/assets/images/cloud-optimization.jpg',
      estimated_read_time: 25,
      difficulty_level: 'advanced',
      is_featured: true,
      status: 'published',
      slug: 'cloud-infrastructure-optimization',
      created_at: '2024-02-01T11:20:00.000Z',
      updated_at: '2024-02-01T11:20:00.000Z'
    },
    {
      id: '5',
      title: 'Building High-Performance Teams',
      description: 'Strategies for creating and maintaining high-performing distributed teams',
      content: '# High-Performance Teams\n\nGuide to building effective remote teams...',
      content_type: 'guide',
      category: 'talent',
      tags: ['Teams', 'Performance', 'Leadership'],
      featured_image: '/assets/images/high-performance-teams.jpg',
      estimated_read_time: 18,
      difficulty_level: 'intermediate',
      is_featured: false,
      status: 'draft',
      slug: 'building-high-performance-teams',
      created_at: '2024-02-05T16:30:00.000Z',
      updated_at: '2024-02-05T16:30:00.000Z'
    }
  ]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [previewMode, setPreviewMode] = useState(false);
  const [previewResource, setPreviewResource] = useState(null);
  const [aiEnhancePrompt, setAiEnhancePrompt] = useState('');
  const [showAiModal, setShowAiModal] = useState(false);
  const [aiTargetResource, setAiTargetResource] = useState(null);

  const [newResource, setNewResource] = useState({
    title: '',
    description: '',
    content: '',
    content_type: 'guide',
    category: 'strategy',
    tags: [],
    featured_image: '',
    estimated_read_time: 5,
    difficulty_level: 'beginner',
    is_featured: false,
    status: 'draft'
  });

  const contentTypes = [
    { value: 'guide', label: 'Guide', icon: 'BookOpen' },
    { value: 'report', label: 'Report', icon: 'FileText' },
    { value: 'checklist', label: 'Checklist', icon: 'CheckSquare' },
    { value: 'whitepaper', label: 'Whitepaper', icon: 'File' },
    { value: 'case-study', label: 'Case Study', icon: 'TrendingUp' }
  ];

  const categories = [
    'strategy', 'compliance', 'talent', 'operations', 'finance', 'technology'
  ];

  const difficultyLevels = [
    { value: 'beginner', label: 'Beginner', color: currentColors.success },
    { value: 'intermediate', label: 'Intermediate', color: currentColors.warning },
    { value: 'advanced', label: 'Advanced', color: currentColors.error }
  ];


  const createResource = async () => {
    console.log('🚀 Creating new resource');
    try {
      const resourceData = {
        ...newResource,
        id: `resource-${Date.now()}`,
        slug: newResource.title.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      console.log('📝 New resource data:', resourceData);
      setResources(prev => [resourceData, ...prev]);
      console.log('✅ Resource added to list');

      setShowCreateModal(false);
      setNewResource({
        title: '',
        description: '',
        content: '',
        content_type: 'guide',
        category: 'strategy',
        tags: [],
        featured_image: '',
        estimated_read_time: 5,
        difficulty_level: 'beginner',
        is_featured: false,
        status: 'draft'
      });
      console.log('🧹 Form reset and modal closed');
      alert('Resource created successfully!');
    } catch (error) {
      console.error('❌ Error creating resource:', error);
      alert('Error creating resource: ' + error.message);
    }
  };

  const updateResource = async () => {
    console.log('🚀 Updating resource:', editingResource?.title);
    try {
      const updatedResource = {
        ...editingResource,
        updated_at: new Date().toISOString()
      };

      console.log('📝 Updated resource data:', updatedResource);
      setResources(prev => prev.map(resource =>
        resource.id === editingResource.id ? updatedResource : resource
      ));
      console.log('✅ Resource updated in list');

      setShowEditModal(false);
      setEditingResource(null);
      console.log('🧹 Edit modal closed and state reset');
      alert('Resource updated successfully!');
    } catch (error) {
      console.error('❌ Error updating resource:', error);
      alert('Error updating resource: ' + error.message);
    }
  };

  const deleteResource = async (resourceId) => {
    if (!confirm('Are you sure you want to delete this resource?')) return;

    console.log('🗑️ Deleting resource with ID:', resourceId);
    try {
      setResources(prev => prev.filter(resource => resource.id !== resourceId));
      console.log('✅ Resource removed from list');
      alert('Resource deleted successfully!');
    } catch (error) {
      console.error('❌ Error deleting resource:', error);
      alert('Error deleting resource: ' + error.message);
    }
  };

  const generateAIContent = async () => {
    console.log('🤖 Generating AI content for resource:', aiTargetResource?.title);
    if (!aiTargetResource || !aiEnhancePrompt.trim()) {
      console.log('❌ Missing target resource or prompt');
      alert('Please provide a prompt for AI enhancement');
      return;
    }

    try {
      console.log('📝 AI prompt:', aiEnhancePrompt);

      // Enhanced AI content generation with custom prompt (instant)
      const enhancedContent = `${aiTargetResource.content}\n\n## AI-Enhanced Content\n\n${aiEnhancePrompt}\n\n### Enhanced Insights\n- Strategic implementation approach based on current market trends\n- Risk mitigation strategies aligned with regulatory requirements\n- Performance optimization techniques for ${aiTargetResource.category} scenarios\n- Compliance considerations and best practices\n\n### Implementation Roadmap\n1. **Assessment Phase**: Evaluate current state and identify gaps\n2. **Planning Phase**: Develop comprehensive implementation strategy\n3. **Execution Phase**: Deploy solutions with proper monitoring\n4. **Optimization Phase**: Continuous improvement and refinement\n\n*This content has been enhanced using AI based on the prompt: "${aiEnhancePrompt}"*`;

      console.log('✨ Generated enhanced content');
      setResources(prev => prev.map(resource =>
        resource.id === aiTargetResource.id
          ? { ...resource, content: enhancedContent, updated_at: new Date().toISOString() }
          : resource
      ));

      setShowAiModal(false);
      setAiEnhancePrompt('');
      setAiTargetResource(null);
      console.log('✅ AI content applied successfully');
      alert('AI content generated successfully!');
    } catch (error) {
      console.error('❌ Error generating AI content:', error);
      alert('Error generating AI content: ' + error.message);
    }
  };

  const filteredResources = resources.filter(resource => {
    const matchesSearch = resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         resource.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || resource.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const ResourceCard = ({ resource }) => (
    <div
      style={{
        background: currentColors.cardBackground,
        border: `1px solid ${currentColors.border}`,
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '16px',
        transition: 'all 0.2s ease',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
        {/* Featured Image */}
        <div
          style={{
            width: '80px',
            height: '80px',
            borderRadius: '8px',
            background: resource.featured_image
              ? `url(${resource.featured_image})`
              : `linear-gradient(135deg, ${currentColors.primary}, ${currentColors.secondary})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '24px',
            flexShrink: 0
          }}
        >
          {!resource.featured_image && (
            <Icon name={contentTypes.find(t => t.value === resource.content_type)?.icon || 'FileText'} size={32} />
          )}
        </div>

        {/* Content */}
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '8px' }}>
            <h3 style={{ 
              color: currentColors.text, 
              margin: 0, 
              fontSize: '18px',
              fontWeight: '600'
            }}>
              {resource.title}
            </h3>
            
            <div style={{ display: 'flex', gap: '4px' }}>
              {resource.is_featured && (
                <span style={{
                  background: currentColors.warning + '20',
                  color: currentColors.warning,
                  padding: '2px 8px',
                  borderRadius: '12px',
                  fontSize: '11px',
                  fontWeight: '600'
                }}>
                  Featured
                </span>
              )}
              
              <span style={{
                background: difficultyLevels.find(d => d.value === resource.difficulty_level)?.color + '20',
                color: difficultyLevels.find(d => d.value === resource.difficulty_level)?.color,
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: '600'
              }}>
                {difficultyLevels.find(d => d.value === resource.difficulty_level)?.label}
              </span>
            </div>
          </div>

          <p style={{ 
            color: currentColors.textSecondary, 
            margin: '0 0 12px 0',
            fontSize: '14px',
            lineHeight: '1.5'
          }}>
            {resource.description}
          </p>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Icon name="Clock" size={14} style={{ color: currentColors.textSecondary }} />
              <span style={{ color: currentColors.textSecondary, fontSize: '12px' }}>
                {resource.estimated_read_time} min read
              </span>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Icon name="Tag" size={14} style={{ color: currentColors.textSecondary }} />
              <span style={{ color: currentColors.textSecondary, fontSize: '12px' }}>
                {resource.category}
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Icon 
                name={resource.status === 'published' ? 'CheckCircle' : 'Clock'} 
                size={14} 
                style={{ color: resource.status === 'published' ? currentColors.success : currentColors.warning }} 
              />
              <span style={{ 
                color: resource.status === 'published' ? currentColors.success : currentColors.warning, 
                fontSize: '12px',
                fontWeight: '500' 
              }}>
                {resource.status.charAt(0).toUpperCase() + resource.status.slice(1)}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => {
                setPreviewResource(resource);
                setPreviewMode(true);
              }}
              style={{
                background: currentColors.primary + '20',
                color: currentColors.primary,
                border: 'none',
                padding: '6px 12px',
                borderRadius: '6px',
                fontSize: '12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <Icon name="Eye" size={12} />
              Preview
            </button>

            <button
              onClick={() => {
                setEditingResource(resource);
                setShowEditModal(true);
              }}
              style={{
                background: currentColors.secondary + '20',
                color: currentColors.secondary,
                border: 'none',
                padding: '6px 12px',
                borderRadius: '6px',
                fontSize: '12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <Icon name="Edit" size={12} />
              Edit
            </button>

            <button
              onClick={() => {
                setAiTargetResource(resource);
                setShowAiModal(true);
              }}
              style={{
                background: currentColors.warning + '20',
                color: currentColors.warning,
                border: 'none',
                padding: '6px 12px',
                borderRadius: '6px',
                fontSize: '12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <Icon name="Sparkles" size={12} />
              AI Enhance
            </button>

            <button
              onClick={() => deleteResource(resource.id)}
              style={{
                background: currentColors.error + '20',
                color: currentColors.error,
                border: 'none',
                padding: '6px 12px',
                borderRadius: '6px',
                fontSize: '12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <Icon name="Trash2" size={12} />
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );


  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ color: currentColors.text, margin: '0 0 8px 0', fontSize: '24px', fontWeight: '700' }}>
          Resources Hub Management
        </h2>
        <p style={{ color: currentColors.textSecondary, margin: 0, fontSize: '14px' }}>
          Create and manage comprehensive resources, guides, and articles for your platform
        </p>
      </div>

      {/* Toolbar */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '24px',
        gap: '16px',
        flexWrap: 'wrap'
      }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {/* Search */}
          <div style={{ position: 'relative' }}>
            <Icon 
              name="Search" 
              size={16} 
              style={{ 
                position: 'absolute', 
                left: '12px', 
                top: '50%', 
                transform: 'translateY(-50%)',
                color: currentColors.textSecondary 
              }} 
            />
            <input
              type="text"
              placeholder="Search resources..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: '10px 10px 10px 40px',
                border: `1px solid ${currentColors.border}`,
                borderRadius: '8px',
                fontSize: '14px',
                backgroundColor: currentColors.background,
                color: currentColors.text,
                width: '250px'
              }}
            />
          </div>

          {/* Category Filter */}
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            style={{
              padding: '10px',
              border: `1px solid ${currentColors.border}`,
              borderRadius: '8px',
              fontSize: '14px',
              backgroundColor: currentColors.background,
              color: currentColors.text
            }}
          >
            <option value="all">All Categories</option>
            {categories.map(category => (
              <option key={category} value={category}>
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={() => setShowCreateModal(true)}
          style={{
            background: currentColors.primary,
            color: 'white',
            border: 'none',
            padding: '12px 20px',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <Icon name="Plus" size={16} />
          Create Resource
        </button>
      </div>

      {/* Resources List */}
      <div>
        {filteredResources.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '60px 20px',
              color: currentColors.textSecondary
            }}
          >
            <Icon name="BookOpen" size={48} style={{ opacity: 0.3, marginBottom: '16px' }} />
            <h3 style={{ margin: '0 0 8px 0', fontSize: '18px' }}>
              No resources found
            </h3>
            <p style={{ margin: 0, fontSize: '14px' }}>
              {searchTerm || filterCategory !== 'all' 
                ? 'Try adjusting your search or filter criteria' 
                : 'Create your first resource to get started'
              }
            </p>
          </div>
        ) : (
          filteredResources.map(resource => (
            <ResourceCard key={resource.id} resource={resource} />
          ))
        )}
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: currentColors.cardBackground,
            borderRadius: '12px',
            padding: '24px',
            width: '100%',
            maxWidth: '900px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 style={{ color: currentColors.text, margin: 0 }}>
                {showCreateModal ? 'Create New Resource' : 'Edit Resource'}
              </h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setShowEditModal(false);
                  setEditingResource(null);
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: currentColors.textSecondary,
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '6px'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = `${currentColors.border}50`}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                ✕
              </button>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: currentColors.text, fontWeight: '500' }}>
                  Title *
                </label>
                <input
                  type="text"
                  value={showCreateModal ? newResource.title : editingResource?.title || ''}
                  onChange={(e) => showCreateModal 
                    ? setNewResource({...newResource, title: e.target.value})
                    : setEditingResource({...editingResource, title: e.target.value})
                  }
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: `1px solid ${currentColors.border}`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    backgroundColor: currentColors.background,
                    color: currentColors.text
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: currentColors.text, fontWeight: '500' }}>
                  Content Type *
                </label>
                <select
                  value={showCreateModal ? newResource.content_type : editingResource?.content_type || ''}
                  onChange={(e) => showCreateModal 
                    ? setNewResource({...newResource, content_type: e.target.value})
                    : setEditingResource({...editingResource, content_type: e.target.value})
                  }
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: `1px solid ${currentColors.border}`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    backgroundColor: currentColors.background,
                    color: currentColors.text
                  }}
                >
                  {contentTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: currentColors.text, fontWeight: '500' }}>
                Description *
              </label>
              <textarea
                value={showCreateModal ? newResource.description : editingResource?.description || ''}
                onChange={(e) => showCreateModal 
                  ? setNewResource({...newResource, description: e.target.value})
                  : setEditingResource({...editingResource, description: e.target.value})
                }
                style={{
                  width: '100%',
                  minHeight: '80px',
                  padding: '12px',
                  border: `1px solid ${currentColors.border}`,
                  borderRadius: '8px',
                  fontSize: '14px',
                  backgroundColor: currentColors.background,
                  color: currentColors.text,
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: currentColors.text, fontWeight: '500' }}>
                Content *
              </label>
              <RichTextEditor
                value={showCreateModal ? newResource.content : editingResource?.content || ''}
                onChange={(content) => showCreateModal 
                  ? setNewResource({...newResource, content})
                  : setEditingResource({...editingResource, content})
                }
                placeholder="Write your resource content using Markdown..."
                minHeight="400px"
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setShowEditModal(false);
                  setEditingResource(null);
                }}
                style={{
                  padding: '10px 20px',
                  border: `1px solid ${currentColors.border}`,
                  borderRadius: '6px',
                  background: 'transparent',
                  color: currentColors.text,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              
              <button
                onClick={showCreateModal ? createResource : updateResource}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '6px',
                  background: currentColors.primary,
                  color: 'white',
                  cursor: 'pointer',
                  opacity: 1
                }}
              >
                {showCreateModal ? 'Create Resource' : 'Update Resource'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewMode && previewResource && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: currentColors.cardBackground,
            borderRadius: '12px',
            padding: '24px',
            width: '100%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 style={{ color: currentColors.text, margin: 0 }}>
                Preview: {previewResource.title}
              </h3>
              <button
                onClick={() => {
                  setPreviewMode(false);
                  setPreviewResource(null);
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: currentColors.textSecondary,
                  cursor: 'pointer',
                  padding: '8px'
                }}
              >
                <Icon name="X" size={20} />
              </button>
            </div>
            
            <div
              style={{
                color: currentColors.text,
                lineHeight: '1.6',
                fontSize: '16px'
              }}
              dangerouslySetInnerHTML={{
                __html: previewResource.content
                  .replace(/^### (.*$)/gim, '<h3>$1</h3>')
                  .replace(/^## (.*$)/gim, '<h2>$1</h2>')
                  .replace(/^# (.*$)/gim, '<h1>$1</h1>')
                  .replace(/\*\*(.*)\*\*/gim, '<strong>$1</strong>')
                  .replace(/\*(.*)\*/gim, '<em>$1</em>')
                  .replace(/\n/gim, '<br/>')
              }}
            />
          </div>
        </div>
      )}

      {/* AI Enhancement Modal */}
      {showAiModal && aiTargetResource && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: currentColors.cardBackground,
            borderRadius: '12px',
            padding: '24px',
            width: '100%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflow: 'auto',
            border: `1px solid ${currentColors.border}`
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 style={{ color: currentColors.text, margin: 0, fontSize: '20px', fontWeight: '600' }}>
                AI Enhancement for "{aiTargetResource.title}"
              </h3>
              <button
                onClick={() => {
                  setShowAiModal(false);
                  setAiTargetResource(null);
                  setAiEnhancePrompt('');
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: currentColors.textSecondary,
                  cursor: 'pointer',
                  padding: '8px'
                }}
              >
                <Icon name="X" size={20} />
              </button>
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <p style={{ color: currentColors.textSecondary, marginBottom: '16px' }}>
                Describe what you want to enhance or add to this resource. The AI will intelligently expand the content based on your prompt.
              </p>
              
              <label style={{ display: 'block', marginBottom: '8px', color: currentColors.text, fontWeight: '500' }}>
                Enhancement Prompt *
              </label>
              <textarea
                value={aiEnhancePrompt}
                onChange={(e) => setAiEnhancePrompt(e.target.value)}
                placeholder="E.g., 'Add more detailed compliance requirements for the Philippines market' or 'Include risk mitigation strategies for remote teams'"
                style={{
                  width: '100%',
                  minHeight: '120px',
                  padding: '12px',
                  border: `1px solid ${currentColors.border}`,
                  borderRadius: '8px',
                  fontSize: '14px',
                  backgroundColor: currentColors.background,
                  color: currentColors.text,
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{
              background: currentColors.background,
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '20px',
              border: `1px solid ${currentColors.border}`
            }}>
              <h4 style={{ color: currentColors.text, margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600' }}>
                Current Content Preview:
              </h4>
              <p style={{ 
                color: currentColors.textSecondary, 
                margin: 0, 
                fontSize: '12px',
                maxHeight: '100px',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {aiTargetResource.content?.substring(0, 200) || aiTargetResource.description}...
              </p>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowAiModal(false);
                  setAiTargetResource(null);
                  setAiEnhancePrompt('');
                }}
                style={{
                  padding: '10px 20px',
                  border: `1px solid ${currentColors.border}`,
                  borderRadius: '6px',
                  background: 'transparent',
                  color: currentColors.text,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              
              <button
                onClick={generateAIContent}
                disabled={!aiEnhancePrompt.trim()}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '6px',
                  background: aiEnhancePrompt.trim() ? currentColors.warning : currentColors.border,
                  color: aiEnhancePrompt.trim() ? 'white' : currentColors.textSecondary,
                  cursor: aiEnhancePrompt.trim() ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <Icon name="Sparkles" size={16} />
                Enhance with AI
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedResourcesManagement;
